import { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import {
  AutoCompleteExtraProps,
  DimensionSelectorExtraProps,
  EditableTableTabFieldProps,
  ExpandableSectionProps,
  FormFieldDefinitionType,
  FormHandles,
  GenericFormSection,
  TextAreaExtraProps,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { ReceiptFormTypes } from "models/formTypes";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ReceiptDraftTodoSelectionFormViewProps } from "models/propTypes/receiptPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { Grid } from "@mui/material";
import { useSearchService } from "serviceHooks";
import { useCostForwardingGenericFormSection } from "components/costForwardingMarking";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const ReceiptDraftTodoSelectionForm: ForwardRefRenderFunction<FormHandles, ReceiptDraftTodoSelectionFormViewProps> = (
  props,
  ref
) => {
  const searchService = useSearchService();
  const {
    receipt,
    receiptCategories,
    receiptPaymentMethods,
    topReceiptPaymentMethods,
    receiptPaymentMethodActions,
    dimensionAmount,
    currency,
    onFilesUploadChange,
    documentDrafts,
    onDocumentDraftSelect,
    receiptCategorySelect,
    receiptCategoryClear,
    onDocumentDraftChanged,
    fileViewer,
    amountsChangeHandler,
    currencyChangeHandler,
  } = props;

  const costForwardingSection = useCostForwardingGenericFormSection<ReceiptFormTypes.ReceiptDraftTodo>();

  const formFields: GenericFormSection<ReceiptFormTypes.ReceiptDraftTodo>[] = [
    {
      label: TranslationKeys.Receipt.AddImages,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      extraProps: {
        alwaysOpen: true,
      } as ExpandableSectionProps,
      innerFields: [
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: "",
          descriptionText: TranslationKeys.Receipt.AddImagesDescription,
          className: cssExports.noBorder,
          innerFields: {
            filesToUpload: {
              label: " ",
              type: FormEnums.FormFieldType.FILEUPLOADER,
              extraProps: { fileUploaderType: "full" },
              onChangeHandler: onFilesUploadChange,
            },
            documentDraft: {
              label: TranslationKeys.Receipt.AddFromDocumentDraft,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              icon: IconDefinitionConstants.WLink,
              onChangeHandler: onDocumentDraftChanged,
              extraProps: {
                referenceFieldName: FormFieldConstants.Receipt.ReceiptDraftTodo.DocumentDraftId,
                items: documentDrafts,
                onItemSelect: onDocumentDraftSelect,
                itemTemplate: (
                  option: AutocompleteListItem,
                  parts: {
                    text: string;
                    highlight: boolean;
                  }[]
                ) => (
                  <Grid key={option.id} container className={option.className}>
                    <Grid container wrap="nowrap">
                      <span className={clsx(cssExports.optionTextContainer, option.icon ? cssExports.hasIcon : "")}>
                        <span className={cssExports.optionText}>
                          <Grid container justifyContent={"space-between"}>
                            <span>
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  className={clsx(
                                    part.highlight ? cssExports.highlightText : "",
                                    option.description ? cssExports.withSubText : ""
                                  )}
                                >
                                  {part.text}
                                </span>
                              ))}
                            </span>
                            {
                              <span className={clsx(cssExports.subText)}>
                                {LocalizationHelperInstance.getFormattedDate(
                                  (option.data as WintSuperkollAPIModel.DocumentDraft).created
                                )}
                              </span>
                            }
                          </Grid>
                        </span>
                        {option.description && <span className={cssExports.subText}>{option.description}</span>}
                      </span>
                    </Grid>
                  </Grid>
                ),
              } as AutoCompleteExtraProps<ReceiptFormTypes.ReceiptDraftTodo>,
            },
          },
        },
      ],
    },
    {
      label: TranslationKeys.Receipt.DraftRefersTo,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      innerFields: {
        receiptCategory: {
          label: TranslationKeys.Common.RefersTo,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          icon: IconDefinitionConstants.WVat,
          extraProps: {
            referenceFieldName: FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryId,
            items: receiptCategories,
            onItemSelect: receiptCategorySelect,
            onItemClear: receiptCategoryClear,
          } as AutoCompleteExtraProps<ReceiptFormTypes.ReceiptDraftTodo>,
        },
        receiptPersons: {
          label: TranslationKeys.Common.Persons,
          type: FormEnums.FormFieldType.EDITABLETABLE,
          hidden: (values: ReceiptFormTypes.ReceiptDraftTodo) =>
            values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.Representation &&
            values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation,
          innerFields: {
            name: {
              label: TranslationKeys.Common.Name,
              icon: IconDefinitionConstants.WUser,
              type: FormEnums.FormFieldType.TEXT,
              widthColumnCount: 5,
            },
            company: {
              label: TranslationKeys.Common.Company,
              type: FormEnums.FormFieldType.TEXT,
            },
          } as FormFieldDefinitionType<ReceiptFormTypes.ReceiptPerson>,
          extraProps: {
            defaultRow: { name: undefined, company: undefined },
            idFieldKey: FormFieldConstants.Receipt.ReceiptPerson.Id,
          } as EditableTableTabFieldProps<ReceiptFormTypes.ReceiptPerson>,
        },
        receiptPurpose: {
          label: TranslationKeys.Common.Purpose,
          type: FormEnums.FormFieldType.TEXTAREA,
          icon: IconDefinitionConstants.WCommentDots,
          hidden: (values: ReceiptFormTypes.ReceiptDraftTodo) =>
            values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.Representation &&
            values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation,
          extraProps: {
            rows: 2,
          } as TextAreaExtraProps,
        },
        otherExplanation: {
          label: TranslationKeys.Receipt.ReceiptOtherExplanationHelptext,
          type: FormEnums.FormFieldType.TEXT,
          maxLength: 300,
          hidden: (values: ReceiptFormTypes.ReceiptDraftTodo) =>
            values.receiptCategoryType !== WintSuperkollAPIModel.ReceiptCategoryType.Other,
        },
      },
    },
    {
      label: TranslationKeys.Common.Information,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      innerFields: {
        amount: {
          label: "",
          type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
          innerFields: {
            amount: {
              label: TranslationKeys.Receipt.AmountInclVat,
              type: FormEnums.FormFieldType.CURRENCY,
              widthColumnCount: 8,
              disabled: receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard,
              onChangeHandler: amountsChangeHandler,
            },
            currency: {
              label: TranslationKeys.Common.Currency,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              widthColumnCount: 4,
              disabled: receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard,
              extraProps: {
                items: searchService.currency,
              } as AutoCompleteExtraProps<ReceiptFormTypes.ReceiptDraftTodo>,
              onChangeHandler: currencyChangeHandler,
            },
            paymentMethod: {
              label: TranslationKeys.Receipt.PaymentMethod,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              widthColumnCount: 12,
              disabled: receipt.paymentMethodType === WintSuperkollAPIModel.ReceiptPaymentMethodType.WintCard,
              extraProps: {
                referenceFieldName: FormFieldConstants.Receipt.ReceiptDraftTodo.PaymentMethodId,
                items: receiptPaymentMethods,
                suggestedItems: topReceiptPaymentMethods.concat(receiptPaymentMethods),
                actions: receiptPaymentMethodActions,
              } as AutoCompleteExtraProps<ReceiptFormTypes.ReceiptDraftTodo>,
            },
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      hidden: (values: ReceiptFormTypes.ReceiptDraftTodo) => !values.showDimensions,
      innerFields: [
        {
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          label: "",
          descriptionText: TranslationKeys.Receipt.DraftDimensionDescription,
          className: cssExports.noBorder,
          innerFields: {
            dimensions: {
              label: "",
              type: FormEnums.FormFieldType.DIMENSIONSELECTOR,
              extraProps: {
                amount: dimensionAmount,
                currency: currency,
                type: DimensionEnums.DimensionCategory.Receipt,
                allowDefaultDimensions: true,
                fileViewer: fileViewer,
              } as DimensionSelectorExtraProps,
            },
          },
        },
      ],
    },
    costForwardingSection,
  ];

  return <GenericForm<ReceiptFormTypes.ReceiptDraftTodo> formRef={ref} {...props} fields={formFields} hideSuccess />;
};

export default forwardRef(ReceiptDraftTodoSelectionForm);
